<template>
  <Multiselect
    ref="selectRef"
    v-model="selectedValue"
    class="multiselect"
    :class=" { 'multiselect_invalid': isInvalid }"
    :options="options"
    :multiple="multiple"
    :taggable="taggable"
    :showLabels="showLabels"
    :internalSearch="internalSearch"
    :hideSelected="hideSelected"
    :disabled="disabled"
    :placeholder="placeholder"
    :optionHeight="optionHeight"
    :searchable="searchable"
    :label="label"
    :allowEmpty="allowEmpty"
    :trackBy="trackBy"
    @SearchChange="handleSearch"
    @open="handleOpen"
    @close="$emit('close')"
  >
    <template #caret>
      <div class="multiselect__select">
        <ChevronDownSvg />
      </div>
    </template>

    <!-- TODO: слот noOptions не работает корректно для multiple=true, если выбраны все варианты и пропс hideSelected=true  -->
    <template
      #noOptions
    >
      <template
        v-if="isLoading"
      >
        <Spin
          size="small"
          class="multiselect__loader"
        />
      </template>
      <span
        v-else
        class="multiselect__empty-wrapper"
      >
        {{ tt('shared.emptyList') }}
      </span>
    </template>

    <template
      #noResult
    >
      <template
        v-if="isLoading"
      >
        <Spin
          size="small"
          class="multiselect__loader"
        />
      </template>
      <span
        v-else
        class="multiselect__empty-wrapper"
      >
        {{ tt('shared.emptyList') }}
      </span>
    </template>

    <!-- TODO: слот options не вышло скрыть по условию !isLoading -->
    <!--    eslint-disable vue/valid-v-for-->

    <template
      v-for="(_, name) in $slots"
      #[name]="slotData"
    >
      <Tooltip
        v-if="slotData.option"
        :key="slotData.id"
        :title="slotData.option.label"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </Tooltip>
      <slot
        v-else
        :name="name"
        v-bind="slotData"
      />
    </template>
  </Multiselect>
</template>

<script lang="ts">
import { Spin } from 'ant-design-vue';
import {
  ComponentPublicInstance,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import Multiselect from 'vue-multiselect';
import isEqual from 'lodash.isequal';

import ChevronDownSvg from '@/assets/svg/16x16/chevron-down.svg';
import tt from '@/i18n/utils/translateText';
import { DEFAULT_DEBOUNCE_DELAY } from '@/constants';
import { Tooltip } from '@/ui';

export default defineComponent({
  name: 'CustomSelect',
  components: {
    Tooltip,
    Multiselect,
    ChevronDownSvg,
    Spin,
  },
  props: {
    value: {
      type: [Array, Object, null] as PropType<any | any[] | null>,
      required: true,
    },
    options: {
      type: Array as PropType<any[]>,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: true,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    internalSearch: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: tt('shared.placeholder.selectFrom'),
    },
    label: {
      type: String,
      default: 'name',
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    optionHeight: {
      type: Number,
      default: 32,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'update',
    'search',
    'open',
    'close',
  ],
  setup(props, { emit }) {
    const selectRef = ref<ComponentPublicInstance<HTMLElement>>();
    const selectedValue = ref(props.value);

    watch(() => props.value, () => {
      selectedValue.value = props.value;
    });

    const emitUpdatedValue = (updatedValue: any | any[] | null) => {
      emit('update', updatedValue);
    };

    watch(() => selectedValue.value, (newValue, oldValue) => {
      if (!isEqual(newValue, oldValue)) {
        emitUpdatedValue(newValue);
      }
    });

    const handleSearch = (searchQuery: string) => {
      emit('search', searchQuery);
    };

    // при открытии панели select`а значение в строке поиска(search) очищается,
    // желаемое поведение - при открытии панели в строке поиска находится значение выбранного элемента списка
    const handleOpen = () => {
      emit('open');
      if (selectRef.value) {
        const element = selectRef.value?.$el;
        const selectInput: HTMLInputElement | null = element.querySelector('.multiselect__input');
        if (selectInput) {
          setTimeout(() => {
            selectInput.value = selectedValue.value?.[props.label] || '';
          }, DEFAULT_DEBOUNCE_DELAY);
        }
      }
    };

    return {
      selectedValue,
      selectRef,

      handleSearch,
      handleOpen,
      tt,
    };
  },
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style lang="scss" src="./styles.scss" />
